import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingCart, ArrowRight } from 'lucide-react';
import { useCart } from '../hooks/useCart';
import { useLanguage } from '../lib/i18n';
import { formatCurrency } from '../lib/currency';
import { cartApi } from '../api/client';

export default function Cart() {
  const { locale } = useLanguage();
  const { items, loading, removeItem, updateQuantity, lastError, clearLastError } = useCart();
  const [discountInfo, setDiscountInfo] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);

  // Load discount info from backend whenever items change
  useEffect(() => {
    if (!items.length) { setDiscountInfo(null); return; }
    setPricingLoading(true);
    cartApi.pricing()
      .then(r => setDiscountInfo(r.data))
      .catch(() => setDiscountInfo(null))
      .finally(() => setPricingLoading(false));
  }, [items]);

  if (loading) return (
    <div className="max-w-3xl mx-auto px-6 py-24 text-center">
      <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin mx-auto" />
    </div>
  );

  const lineCalcs = discountInfo?.lineCalcs ?? [];
  const displayTotal = discountInfo?.finalTotal ?? items.reduce((s, i) => s + i.priceSnapshot * i.quantity, 0);

  return (
    <div data-testid="cart-page" className="max-w-3xl mx-auto px-6 md:px-12 py-12">
      <div className="mb-8">
        <h1 className="font-serif text-4xl font-light text-foreground mb-1">Winkelmand</h1>
        <div className="w-10 h-px bg-accent mt-3" />
      </div>

      {lastError && (
        <div
          data-testid="cart-error-banner"
          className="mb-4 rounded-2xl border border-destructive/30 bg-destructive/5 text-destructive text-sm px-4 py-3 flex items-center justify-between gap-3"
          role="alert"
        >
          <span className="flex-1">{lastError}</span>
          <button
            data-testid="cart-error-dismiss"
            onClick={clearLastError}
            className="text-xs underline opacity-70 hover:opacity-100"
          >
            sluiten
          </button>
        </div>
      )}

      {items.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-border rounded-2xl">
          <ShoppingCart className="w-10 h-10 text-muted-foreground/30 mx-auto mb-4" />
          <p data-testid="empty-cart" className="font-serif text-xl text-muted-foreground mb-4">Uw winkelmand is leeg</p>
          <Link to="/catalog" className="inline-flex items-center gap-2 text-sm text-primary font-medium hover:underline">
            Ontdek onze producten <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(item => {
            // Find matching lineCalc for this item
            const lc = lineCalcs.find(l => l.productId === item.productId && l.cartId === item.cartId) || {};
            const effectivePrice = lc.effectiveUnitPrice ?? item.priceSnapshot;
            const hasStack = (lc.freeUnits ?? 0) > 0;
            const lineTotal = effectivePrice * item.quantity;

            return (
              <div key={item._id} data-testid={`cart-item-${item._id}`} className="flex items-center gap-4 p-4 bg-card border border-border rounded-2xl shadow-sm">
                {item.imageUrl && (
                  <Link to={`/products/${item.slug}`} className="shrink-0">
                    <img src={item.imageUrl} alt="" className="w-16 h-16 rounded-xl object-cover" />
                  </Link>
                )}
                <div className="flex-1 min-w-0">
                  <Link to={`/products/${item.slug}`} className="font-serif font-medium text-foreground truncate block hover:text-primary transition-colors">
                    {item[`nameSnapshot_${locale}`] || item.nameSnapshot_nl}
                  </Link>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {hasStack ? (
                      <>
                        <span className="line-through mr-1">{formatCurrency(item.priceSnapshot, locale)}</span>
                        <span className="text-primary font-medium">{formatCurrency(effectivePrice, locale)}</span>
                        <span> per stuk</span>
                      </>
                    ) : (
                      <span>{formatCurrency(item.priceSnapshot, locale)} per stuk</span>
                    )}
                  </p>
                  {hasStack && (
                    <span data-testid={`stack-badge-${item._id}`} className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                      {lc.freeUnits} stuk{lc.freeUnits > 1 ? 's' : ''} gratis
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid={`qty-minus-${item._id}`} onClick={() => updateQuantity(item._id, item.quantity - 1)}
                    className="w-8 h-8 flex items-center justify-center rounded-full border border-border hover:bg-muted transition-colors">
                    <Minus className="w-3 h-3" />
                  </button>
                  <span data-testid={`qty-${item._id}`} className="w-8 text-center text-sm font-semibold">{item.quantity}</span>
                  <button data-testid={`qty-plus-${item._id}`} onClick={() => updateQuantity(item._id, item.quantity + 1)}
                    className="w-8 h-8 flex items-center justify-center rounded-full border border-border hover:bg-muted transition-colors">
                    <Plus className="w-3 h-3" />
                  </button>
                </div>
                <span className="w-24 text-right text-sm font-semibold text-foreground">
                  {formatCurrency(lineTotal, locale)}
                </span>
                <button data-testid={`remove-${item._id}`} onClick={() => removeItem(item._id)}
                  className="p-2 text-muted-foreground hover:text-destructive transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}

          {/* Discount summary */}
          <div className="mt-8 border-t border-border pt-6 space-y-3">
            {discountInfo && discountInfo.totalStackSavingExclVat > 0 && (
              <div data-testid="stack-discount-row" className="flex justify-between text-sm text-green-700">
                <span>11+1 stapelkorting (excl. BTW)</span>
                <span>-{formatCurrency(discountInfo.totalStackSavingExclVat, locale)}</span>
              </div>
            )}
            {discountInfo?.discountType === 'profile' && (
              <div data-testid="profile-discount-row" className="flex justify-between text-sm text-primary">
                <span>Uw persoonlijke korting ({discountInfo.appliedRate}%, excl. BTW)</span>
                <span>-{formatCurrency(discountInfo.discountExclVat, locale)}</span>
              </div>
            )}
            {discountInfo?.discountType === 'b2b_standard' && (
              <div data-testid="b2b-discount-row" className="flex justify-between text-sm text-primary">
                <span>B2B korting ({discountInfo.appliedRate}%, excl. BTW)</span>
                <span>-{formatCurrency(discountInfo.discountExclVat, locale)}</span>
              </div>
            )}
            {discountInfo?.vatReversed && (
              <div data-testid="vat-reversed-row" className="rounded-lg bg-blue-50 border border-blue-200 text-blue-800 text-xs px-3 py-2">
                <p className="font-semibold">BTW verlegd — intracommunautaire levering, Art. 39bis W.BTW.</p>
                <p className="mt-0.5">Geen BTW verschuldigd op deze bestelling.</p>
              </div>
            )}
            {discountInfo?.shippingCost > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Verzendkosten</span>
                <span>{formatCurrency(discountInfo.shippingCost, locale)}</span>
              </div>
            )}
            <div className="flex items-center justify-between pt-2 border-t border-border">
              <span className="font-serif text-xl text-foreground">Totaal</span>
              <span data-testid="cart-total" className="text-xl font-semibold text-foreground">
                {formatCurrency(discountInfo?.grandTotal ?? displayTotal, locale)}
              </span>
            </div>
            <Link to="/checkout" data-testid="checkout-btn"
              className="w-full flex items-center justify-center gap-2.5 py-3.5 bg-primary text-primary-foreground rounded-full font-semibold hover:bg-primary/90 transition-all active:scale-95 shadow-sm">
              Doorgaan naar afrekenen <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
